/**
 * SectionLoader — quick, subtle loading state for any section in the app.
 *
 * Old behavior (pre 2026-04-29): wrapped the elaborate Loader-6 (REC →
 * laptop → play 3-second animation cycle). User feedback during the
 * 2026-04-29 session: "the loading icon I wanted, it still shows" —
 * the heavy Loader-6 attracted attention even when the actual load was
 * a few hundred ms, making pages feel "stuck" on what was a brief
 * loading state.
 *
 * New behavior (2026-04-29):
 *   - Renders a compact 2-ring brand-cyan spinner instead of Loader-6
 *   - Delays visibility 300ms so fast loads (< 300ms) show NOTHING
 *   - When shown, the spinner fades in over 200ms so even longer loads
 *     don't pop into view abruptly
 *   - Same back-compat props (label, scale, color, padding, className)
 *     so the 20+ existing call sites don't need touching
 *
 * Loader-6 is still available at @/components/ui/loader-6 for splash
 * screens or branded loading moments — just isn't the default
 * in-section loader anymore.
 */
import { useEffect, useState } from "react";

interface SectionLoaderProps {
  /** Optional helper text shown below the spinner. */
  label?: string;
  /** Scale factor (default 1). 0.7 = compact, 1.5 = hero-fill. */
  scale?: number;
  /** Override the spinner color. Defaults to BHG brand cyan. */
  color?: string;
  /** Vertical padding classes. Default "py-12". */
  padding?: string;
  className?: string;
  /**
   * Delay (ms) before the loader becomes visible. Defaults to 300.
   * Pages that resolve before this never paint the loader at all —
   * eliminates the perceived "stuck" feeling on quick chunk loads.
   * Set to 0 if you need an immediately-visible loader (e.g., a
   * full-page route transition where the user already left the
   * previous page).
   */
  delayMs?: number;
}

const BHG_PRIMARY = "hsl(188, 85%, 62%)";

export default function SectionLoader({
  label,
  scale = 1,
  color = BHG_PRIMARY,
  padding = "py-12",
  className = "",
  delayMs = 300,
}: SectionLoaderProps) {
  const [visible, setVisible] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) return;
    const t = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  // Reserve the wrapper height even before visible=true so the spinner
  // popping in doesn't shift surrounding layout. Just controls opacity.
  const sizePx = Math.round(28 * scale);
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2.5 ${padding} ${className}`}
      role="status"
      aria-live="polite"
      style={{
        opacity: visible ? 1 : 0,
        transition: "opacity 200ms ease-out",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          borderRadius: "50%",
          border: `2px solid ${color}33`, // outer ring at ~20% alpha
          borderTopColor: color, // moving arc at full alpha
          animation: "bhg-spin 0.7s linear infinite",
          display: "inline-block",
        }}
      />
      {label && (
        <p className="text-xs sm:text-sm text-muted-foreground font-medium tracking-wide">
          {label}
        </p>
      )}
      {/* Inline keyframes — self-contained, no global CSS dependency.
          Tailwind's animate-spin uses different easing; we want a
          tighter linear rotation here. */}
      <style>{`
        @keyframes bhg-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
